import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Progress } from "@/shared/ui/progress";
import { AlertTriangle, Zap, TrendingUp } from "lucide-react";
import type { Anomaly, ControlAction, OverviewModel } from "../types";

interface OverviewTabProps {
  anomalies: Anomaly[];
  controlActions: ControlAction[];
  models: OverviewModel[];
  showMockData: boolean;
}

export function OverviewTab({ 
  anomalies, 
  controlActions, 
  models, 
  showMockData 
}: OverviewTabProps) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Live Anomalies */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" />
              Live Anomalies
            </CardTitle>
            <CardDescription>Real-time detected anomalies requiring attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {showMockData ? anomalies.map((anomaly) => (
                <div key={anomaly.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex items-center gap-3">
                    <div className={`h-3 w-3 rounded-full ${
                      anomaly.severity === "high" ? "bg-destructive" :
                      anomaly.severity === "medium" ? "bg-warning" : "bg-chart-4"
                    }`} />
                    <div>
                      <p className="font-medium text-sm">{anomaly.type}</p>
                      <p className="text-xs text-muted-foreground">{anomaly.station} • {anomaly.timestamp}</p>
                    </div>
                  </div>
                  <Badge variant={anomaly.resolved ? "secondary" : "outline"}>
                    {anomaly.resolved ? "Resolved" : "Active"}
                  </Badge>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-4">No anomalies detected</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Control Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-chart-2" />
              Recent AI Actions
            </CardTitle>
            <CardDescription>Latest machine control interventions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {showMockData ? controlActions.map((action) => (
                <div key={action.id} className="flex items-center justify-between p-3 rounded-lg border bg-card">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{action.action}</p>
                    <p className="text-xs text-muted-foreground">{action.station} • {action.timestamp}</p>
                  </div>
                  <Badge variant={action.status === 'executed' ? "default" : "secondary"}>
                    {action.status}
                  </Badge>
                </div>
              )) : (
                <p className="text-sm text-muted-foreground text-center py-4">No AI actions recorded</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Model Performance Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Model Performance by Station
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {showMockData && models.filter(m => m.status === 'deployed').map((model) => (
              <Card key={model.id} className="border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="text-primary border-primary/30">
                      {model.station}
                    </Badge>
                    <div className="flex items-center gap-1">
                      <div className="h-2 w-2 rounded-full bg-success" />
                      <span className="text-xs text-muted-foreground">Live</span>
                    </div>
                  </div>
                  <h4 className="font-semibold text-sm mb-2">{model.name}</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Accuracy</span>
                      <span className="font-medium text-success">{model.accuracy}%</span>
                    </div>
                    <Progress value={model.accuracy} className="h-1.5" />
                  </div>
                  {model.anomalies > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-warning">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-xs">{model.anomalies} anomalies detected</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
